import type { CartItem, DeliveryMethod } from './types'
import {
  currencySignHtml,
  renderWithCurrency
} from 'application/utils/currency'
import {
  addToCart,
  clearCart,
  getCartItems,
  removeFromCart,
  updateCartItem
} from './_service'

// Дизайн, который присваивается электронному сертификату, если покупатель
// его не выбирал (коды — Certificate::DESIGNS, выбор — в блоке data-cart-designs).
const DEFAULT_DESIGN = 'gift'
const MAX_CUSTOM_AMOUNT = 30_000
const CUSTOM_AMOUNT_STEP = 1000
// localStorage-ключ id позиции «своя сумма»: без него после перезагрузки
// следующий «+» степпера создавал бы дубль вместо изменения существующей.
const CUSTOM_ITEM_ID_KEY = 'cart-custom-item-id'
const designIllustrationUrl = (code: string) =>
  `/images/certificates/designs/illustrations/${code}@1x.webp`

export class CartList {
  private container: HTMLElement
  private items: CartItem[] = []
  private deliveryCost: number = 0
  private baseDeliveryCost: number = 0
  private discountAmount: number = 0
  private totalPrice: number = 0
  private totalPriceElement: HTMLElement
  private deliveryCostElement: HTMLElement | null = null
  private itemsContainer: HTMLElement
  private addElement: HTMLElement | null = null
  private summaryListEl: HTMLElement | null = null
  private designNames: Record<string, string> = {}
  private customItemId: number | null = null
  // Восстановление позиции «своя сумма» по признакам — одноразовое (на
  // загрузку): иначе после удаления кастомной позиции степпер «усыновил» бы
  // первый попавшийся сертификат со своей суммой.
  private customRestoreAttempted = false
  private amounts: number[] = []
  private liveRegionResetTimer: number | null = null
  private onItemsChange?: (_items: CartItem[]) => void

  constructor(
    container: HTMLElement,
    onItemsChange?: (_items: CartItem[]) => void
  ) {
    this.container = container
    this.onItemsChange = onItemsChange

    this.init()
  }

  private init() {
    console.debug('CartList initialized')

    this.deliveryCostElement = this.container.querySelector<HTMLElement>(
      '[data-delivery-cost]'
    )
    if (this.deliveryCostElement) {
      this.baseDeliveryCost = parseFloat(
        this.deliveryCostElement.dataset.deliveryCost || '0'
      )
      this.deliveryCost = this.baseDeliveryCost
    }

    const discountElement =
      this.container.querySelector<HTMLElement>('[data-discount]')
    if (discountElement) {
      this.discountAmount = parseFloat(discountElement.dataset.discount || '0')
    }

    this.totalPriceElement =
      this.container.querySelector<HTMLElement>('[data-total-price]')!
    this.itemsContainer =
      this.container.querySelector<HTMLElement>('[data-cart-items]')!
    this.addElement =
      this.container.querySelector<HTMLElement>('[data-cart-add]')
    this.summaryListEl = this.container.querySelector<HTMLElement>(
      '[data-cart-summary-list]'
    )

    try {
      this.amounts = JSON.parse(this.container.dataset.amounts || '[]')
    } catch {
      this.amounts = []
    }

    const designsElement = document.querySelector<HTMLElement>(
      '[data-cart-designs]'
    )
    try {
      this.designNames = Object.fromEntries(
        JSON.parse(designsElement?.dataset.designs || '[]').map(
          (design: { code: string; name: string }) => [design.code, design.name]
        )
      )
    } catch {
      this.designNames = {}
    }

    this.updateItems(false)
    console.debug('items', this.items)

    this.container.addEventListener('click', this.onClick)
  }

  public getItems(): CartItem[] {
    return this.items
  }

  // Перерендер позиций из localStorage без пересчёта суммы и без
  // onItemsChange — например, после смены дизайна в блоке выбора.
  public refresh(): void {
    this.itemsContainer.innerHTML = ''
    this.items = getCartItems()
    this.renderItems()
    this.renderSummary()
  }

  public setDeliveryMethod(method: DeliveryMethod): void {
    this.deliveryCost = method === 'courier' ? this.baseDeliveryCost : 0

    if (this.deliveryCostElement) {
      renderWithCurrency(
        this.deliveryCostElement,
        this.deliveryCost.toLocaleString('ru-RU', {
          style: 'currency',
          currency: this.items[0]?.currency || 'RUB',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
      )
    }

    this.calculateTotalPrice(true)
  }

  public reset(): void {
    this.clearCart()
  }

  private updateItems(announceTotalPrice: boolean = false) {
    this.itemsContainer.innerHTML = ''
    this.items = getCartItems()
    this.ensureDigitalDesigns()
    // Связь степпера со своей суммой восстанавливается один раз при загрузке.
    if (this.customItemId === null && !this.customRestoreAttempted) {
      this.customRestoreAttempted = true
      this.restoreCustomItemId()
    }
    // Позиция со своей суммой могла быть удалена (крестик, очистка) —
    // тогда степпер сбрасывается и следующий «+» создаст новую.
    if (this.customItemId !== null) {
      const customItem = this.items.find((i) => i.id === this.customItemId)
      if (customItem) {
        this.renderCustomAmount(customItem.price)
      } else {
        this.setCustomItemId(null)
        this.renderCustomAmount(0)
      }
    }
    this.renderItems()
    this.renderSummary()
    this.calculateTotalPrice(announceTotalPrice)
    this.updateAddVisibility()

    if (this.onItemsChange) {
      this.onItemsChange(this.items)
    }
  }

  // Состав заказа компактно, над строкой «К оплате»: позиции с дизайнами.
  private renderSummary() {
    if (!this.summaryListEl) return

    this.summaryListEl.innerHTML = this.items
      .map((item) => {
        const designName =
          item.type === 'digital' && item.parameters?.design
            ? this.designNames[item.parameters.design]
            : null
        const amount = `${(item.price * item.quantity).toLocaleString('ru-RU')} ${item.currency === 'BYN' ? 'BYN' : '₽'}`

        return `<li class="cart-list__summary-item">
            <span class="cart-list__summary-title">${item.title}</span>
            ${designName ? `<span class="cart-list__summary-design">Дизайн: ${designName}</span>` : ''}
            <span class="cart-list__summary-price">${amount}</span>
          </li>`
      })
      .join('')
  }

  // Электронному сертификату без выбранного дизайна присваиваем дизайн по
  // умолчанию — сервер сохранит его в certificates.design.
  private ensureDigitalDesigns() {
    this.items.forEach((item) => {
      if (item.type === 'digital' && !item.parameters?.design) {
        item.parameters = { ...item.parameters, design: DEFAULT_DESIGN }
        updateCartItem(item)
      }
    })
  }

  // Номиналы в RUB добавлять можно только в рублёвую корзину (у BYN свои цены).
  private updateAddVisibility() {
    if (!this.addElement) return

    const hasByn = this.items.some((item) => item.currency === 'BYN')
    this.addElement.classList.toggle(
      'is-hidden',
      this.amounts.length === 0 || hasByn
    )
  }

  private renderItems() {
    const documentFragment = document.createDocumentFragment()
    this.items.forEach((item) => {
      const itemElement = this.createCartItemElement(item)
      documentFragment.appendChild(itemElement)
    })
    this.itemsContainer.appendChild(documentFragment)
  }

  private calculateTotalPrice(announceTotalPrice: boolean = false) {
    const totalLabel =
      this.container.querySelector<HTMLElement>('[data-total-label]')
    if (totalLabel)
      totalLabel.textContent = this.items.some(
        (item) => item.currency === 'BYN'
      )
        ? 'Номинал сертификатов'
        : 'К оплате'
    const itemsTotal = this.items.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    )
    if (new Set(this.items.map((item) => item.currency || 'RUB')).size > 1) {
      this.totalPriceElement.textContent =
        'Сертификаты разных валют: оформите отдельные заказы'
      return
    }
    this.totalPrice = itemsTotal + this.deliveryCost - this.discountAmount
    this.updateTotalPriceUI(announceTotalPrice)
  }

  private updateTotalPriceUI(announceTotalPrice: boolean = false) {
    if (this.totalPriceElement) {
      const totalPriceText = this.totalPrice.toLocaleString('ru-RU', {
        style: 'currency',
        currency: this.items[0]?.currency || 'RUB',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })

      const hasChanged = this.totalPriceElement.textContent !== totalPriceText

      if (announceTotalPrice && hasChanged) {
        this.totalPriceElement.setAttribute('aria-live', 'polite')
      }

      renderWithCurrency(this.totalPriceElement, totalPriceText)

      if (announceTotalPrice && hasChanged) {
        if (this.liveRegionResetTimer) {
          window.clearTimeout(this.liveRegionResetTimer)
        }

        this.liveRegionResetTimer = window.setTimeout(() => {
          this.totalPriceElement.setAttribute('aria-live', 'off')
          this.liveRegionResetTimer = null
        }, 1000)
      }
    }
  }

  private onClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement

    if (target.closest('[data-cart-item-delete]')) {
      this.deleteItem(target.closest('[data-cart-item]') as HTMLElement)
      return
    }

    if (target.closest('[data-delete-all]')) {
      this.clearCart()
      return
    }

    const addButton = target.closest<HTMLElement>('[data-cart-add-amount]')
    if (addButton) {
      this.addAmount(parseInt(addButton.dataset.cartAddAmount || '0', 10))
      return
    }

    if (target.closest('[data-cart-add-decrease]')) {
      this.changeCustomAmount(-CUSTOM_AMOUNT_STEP)
      return
    }

    if (target.closest('[data-cart-add-increase]')) {
      this.changeCustomAmount(CUSTOM_AMOUNT_STEP)
    }
  }

  // Своя сумма — это одна позиция заказа: первый «+» добавляет сертификат
  // на 1 000 ₽, дальнейшие клики меняют номинал той же позиции; «−» ниже
  // 1 000 ₽ убирает её из заказа.
  private changeCustomAmount(delta: number) {
    const customItem = this.items.find((i) => i.id === this.customItemId)
    const next = Math.min(
      Math.max((customItem?.price ?? 0) + delta, 0),
      MAX_CUSTOM_AMOUNT
    )

    if (!customItem) {
      if (next <= 0) return

      const newItem: CartItem = {
        id: Date.now(),
        title: 'Электронный сертификат',
        type: 'digital',
        quantity: 1,
        price: next,
        parameters: { design: DEFAULT_DESIGN },
        currency: 'RUB',
        image: ''
      }
      addToCart(newItem)
      this.setCustomItemId(newItem.id)
    } else if (next <= 0) {
      removeFromCart(customItem)
      this.setCustomItemId(null)
    } else {
      customItem.price = next
      updateCartItem(customItem)
    }

    this.updateItems(true)
  }

  private renderCustomAmount(value: number) {
    const valueEl = this.addElement?.querySelector<HTMLElement>(
      '[data-cart-add-value]'
    )
    if (valueEl) {
      valueEl.textContent = `${value.toLocaleString('ru-RU')} ₽`
    }
  }

  // id позиции «своя сумма» жил только в памяти — после перезагрузки степпер
  // терял позицию и «+» создавал дубль. Восстанавливаем связь: сначала по
  // сохранённому id, а для позиций, созданных до этого фикса, — по признакам
  // кастомной позиции (электронный RUB-сертификат с ценой не из номиналов).
  private restoreCustomItemId() {
    let storedId = 0
    try {
      storedId = Number(localStorage.getItem(CUSTOM_ITEM_ID_KEY))
    } catch {
      storedId = 0
    }
    if (storedId && this.items.some((item) => item.id === storedId)) {
      this.customItemId = storedId
      return
    }
    const custom = this.items.find(
      (item) =>
        item.type === 'digital' &&
        item.currency === 'RUB' &&
        item.quantity === 1 &&
        !this.amounts.includes(item.price)
    )
    this.customItemId = custom ? custom.id : null
  }

  private setCustomItemId(id: number | null) {
    this.customItemId = id
    // localStorage может быть недоступен (квота, приватный режим): id остаётся
    // в памяти, а корзина перерендеривается как обычно.
    try {
      if (id === null) {
        localStorage.removeItem(CUSTOM_ITEM_ID_KEY)
      } else {
        localStorage.setItem(CUSTOM_ITEM_ID_KEY, String(id))
      }
    } catch {
      // ignore
    }
  }

  private addAmount(amount: number) {
    if (amount <= 0 || amount > MAX_CUSTOM_AMOUNT) return

    addToCart({
      id: Date.now(),
      title: 'Электронный сертификат',
      type: 'digital',
      quantity: 1,
      price: amount,
      parameters: { design: DEFAULT_DESIGN },
      currency: 'RUB',
      image: ''
    })
    this.updateItems(true)
  }

  private deleteItem(itemEl: HTMLElement) {
    if (itemEl) {
      const itemId = itemEl.getAttribute('data-id')
      if (itemId) {
        const item = this.items.find((i) => i.id.toString() === itemId)
        if (item) {
          removeFromCart(item)
          this.updateItems(true)
        }
      }
    }
  }

  private clearCart() {
    clearCart()
    this.updateItems(true)
  }

  private createCartItemElement(item: CartItem): HTMLElement {
    const template = document.createElement('template')
    template.innerHTML = this.createCartItemTemplate(item).trim()
    return template.content.firstElementChild as HTMLElement
  }

  private createCartItemTemplate(item: CartItem): string {
    const parameters = Object.entries(item.parameters || {})
      .filter(([key]) => key !== 'design')
      .map(([, value]) => value)
      .join(', ')
    const note =
      item.type === 'digital'
        ? '<p class="cart-item__text">придёт на почту</p>'
        : ''

    // Электронный — миниатюра присвоенного дизайна; у физического
    // (коробочного) — переданная картинка, с запасным вариантом.
    let imageSrc: string
    let imageAlt = item.title
    if (item.type === 'digital') {
      const design = item.parameters?.design || DEFAULT_DESIGN
      imageSrc = designIllustrationUrl(design)
      imageAlt = item.title
    } else {
      imageSrc = item.image || '/images/certificates/box@1x.png'
    }

    return `<div class="cart-item" data-cart-item data-id="${item.id}">
              <div class="cart-item__img${item.type === 'digital' ? ' cart-item__img_cover' : ''}">
                <img loading="lazy" src="${imageSrc}" alt="${imageAlt}">
              </div>

              <div class="cart-item__info">
                <button type="button" class="cart-item__btn-delete" data-cart-item-delete>
                  <span class="visually-hidden">Удалить товар из корзины</span>
                  <svg>
                    <use href="#close"></use>
                  </svg>
                </button>

                <p class="cart-item__title">
                  ${item.title}
                </p>

                ${note}

                <p class="cart-item__text">
                  ${parameters}
                </p>

                <p class="cart-item__price">${item.price.toLocaleString('ru-RU')} ${currencySignHtml(item.currency === 'BYN' ? 'BYN' : '₽')}</p>
                </div>
              </div>`
  }
}
